<template>
  <div class="good-practice-detail-page">
    <WeCircularNavbar />

    <section class="detail-section">
      <div class="detail-container">
        <button class="back-button" type="button" @click="$router.back()">
          ← Back
        </button>

        <div class="hero-panel">
          <div class="hero-content">
            <div class="hero-top">
              <div class="hero-image-wrapper">
                <img :src="currentItem.image || defaultImage" :alt="currentItem.name" class="hero-image" />
              </div>

              <div class="hero-text">
                <h1 class="hero-title">
                  {{ currentItem.name }}
                </h1>
              </div>
            </div>

            <div class="accordion-card">
              <button class="accordion-row accordion-row--open-all" type="button" @click="toggleAll">
                <span>Open all</span>
                <span class="accordion-icon">{{ allOpen ? '−' : '+' }}</span>
              </button>

              <div v-for="section in sections" :key="section.key" class="accordion-item">
                <button class="accordion-row" type="button" @click="toggleSection(section.key)">
                  <span>{{ section.label }}</span>
                  <span class="accordion-icon">{{ isOpen(section.key) ? '−' : '+' }}</span>
                </button>

                <div v-if="isOpen(section.key)" class="accordion-content">
                  <p class="accordion-text">{{ section.value || '-' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <WeCircularFooter />
      </div>
    </section>
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'
import defaultImage from '../../assets/img/we-circular/good-practices/1-1@4x.png'
import { goodPracticesData } from './goodPracticesData.js'

export default {
  name: 'GoodPracticeDetailPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      defaultImage,
      openSections: []
    }
  },
  computed: {
    allItems() {
      if (!goodPracticesData || typeof goodPracticesData !== 'object') {
        return []
      }

      return Object.values(goodPracticesData).flat()
    },

    routeSlug() {
      return this.$route.params.slug || ''
    },

    currentItem() {
      const found = this.allItems.find(item => this.slugify(item.name) === this.routeSlug)

      return (
        found || {
          name: 'Good practice',
          image: '',
          about: '',
          scope: '',
          goals: '',
          keyActors: '',
          methodology: '',
          funding: '',
          owner: '',
          contact: ''
        }
      )
    },

    sections() {
      return [
        {
          key: 'about',
          label: 'About this good practice',
          value: this.currentItem.description
        },
        {
          key: 'scope',
          label: 'Scope of this good practice',
          value: this.currentItem.scope
        },
        {
          key: 'goals',
          label: 'Goals',
          value: this.currentItem.goals
        },
        {
          key: 'keyActors',
          label: 'Key actors/partners involved',
          value: this.currentItem.keyActorsPartnersInvolved
        },
        {
          key: 'methodology',
          label: 'Methodology/approach',
          value: this.currentItem.methodologyApproach
        },
        {
          key: 'funding',
          label: 'Funding',
          value: this.currentItem.funding
        },
        {
          key: 'owner',
          label: 'Good practice owner',
          value: this.currentItem.goodPracticeOwner
        },
        {
          key: 'contact',
          label: 'Contact',
          value: this.currentItem.contact
        }
      ]
    },

    allOpen() {
      return this.openSections.length === this.sections.length
    }
  },
  methods: {
    slugify(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },

    toggleSection(key) {
      if (this.openSections.includes(key)) {
        this.openSections = this.openSections.filter(item => item !== key)
      } else {
        this.openSections = [...this.openSections, key]
      }
    },

    isOpen(key) {
      return this.openSections.includes(key)
    },

    toggleAll() {
      if (this.allOpen) {
        this.openSections = []
      } else {
        this.openSections = this.sections.map(section => section.key)
      }
    }
  }
}
</script>

<style scoped>
.good-practice-detail-page {
  min-height: 100vh;
  background: #f3f3f3;
}

.detail-section {
  padding: 0 0 40px;
}

.detail-container {
  width: 100%;
}

.back-button {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  align-self: flex-start;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin: 0 0 22px 0;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  text-align: left;
}

.hero-panel {
  width: min(1240px, calc(100% - 48px));
  margin: 0 auto;
  padding: 42px 0 28px;
  background: #0b5a58;
  background-image: url('../../assets/img/we-circular/9-16@4x.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.hero-content {
  width: min(900px, calc(100% - 64px));
  margin: 0 auto;
}

.hero-top {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 26px;
  align-items: start;
  margin-bottom: 34px;
}

.hero-image-wrapper {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.14);
}

.hero-image {
  display: block;
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.hero-text {
  min-width: 0;
}

.hero-title {
  margin: 0;
  color: #ffffff;
  font-size: 54px;
  line-height: 1.05;
  font-weight: 500;
  letter-spacing: -0.03em;
  text-align: left;
}

.accordion-card {
  border-radius: 12px;
  background: #f7f7f7;
  overflow: hidden;
}

.accordion-item {
  border-top: 1px solid #d6d9df;
}

.accordion-item:first-of-type {
  border-top: 1px solid #d6d9df;
}

.accordion-row {
  width: 100%;
  min-height: 56px;
  padding: 0 18px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #2c3142;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.accordion-row--open-all {
  border-bottom: 1px solid #d6d9df;
}

.accordion-icon {
  flex-shrink: 0;
  margin-left: 18px;
  font-size: 18px;
  line-height: 1;
  color: #5c6377;
}

.accordion-content {
  padding: 0 18px 18px;
  border-top: 1px solid #eceef2;
}

.accordion-text {
  margin: 0;
  color: #3b4356;
  font-size: 12px;
  line-height: 1.7;
  white-space: pre-line;
  text-align: left;
}

@media (max-width: 1100px) {
  .hero-title {
    font-size: 42px;
  }
}

@media (max-width: 900px) {
  .hero-panel {
    width: min(100% - 24px, 1240px);
    padding-top: 28px;
  }

  .hero-content {
    width: min(100% - 32px, 900px);
  }

  .hero-top {
    grid-template-columns: 1fr;
  }

  .hero-image-wrapper {
    max-width: 260px;
  }

  .hero-title {
    font-size: 34px;
  }

  .back-button {
    margin-left: 12px;
  }
}

@media (max-width: 520px) {
  .hero-title {
    font-size: 26px;
  }

  .accordion-row {
    min-height: 50px;
    font-size: 12px;
    padding: 0 14px;
  }

  .accordion-content {
    padding: 0 14px 14px;
  }

  .accordion-text {
    font-size: 11px;
  }
}
</style>
